import { DefinedComponent } from '../camel/camel-catalog-index';
import { BaseEntity, EntityType } from '../entities';
import { KaotoSchemaDefinition } from '../kaoto-schema';
import { NodeLabelType } from '../settings/settings.model';
import { IClipboardContent } from '../visualization/clipboard';
import { NodeIdentity } from './node-identity';

/**
 * BaseVisualCamelEntity
 *
 * This interface is used to represent a visual Camel entity.
 * f.i. Camel Route, Kamelet, KameletBinding, etc.
 * All dedicated Camel code should be implemented using this interface.
 */
export interface BaseVisualEntity extends BaseEntity {
  id: string;
  type: EntityType;

  /** Return the root path of the entity */
  getRootPath: () => string;

  getId: () => string;

  setId: (id: string) => void;

  /** Given a path, get the component label */
  getNodeLabel: (path?: string, labelType?: NodeLabelType, ids?: IVisualizationNodeIds) => string;

  /**
   * Async, ID-based schema resolution using the Dynamic Catalog.
   * Accepts the three node identifiers and returns the fully resolved schema.
   * DSL-specific logic is owned by each concrete BaseVisualEntity implementation.
   */
  fetchNodeSchema(ids?: IVisualizationNodeIds): Promise<KaotoSchemaDefinition['schema'] | undefined>;

  /**
   * Async, path + ID-based definition resolution.
   * Returns the fully normalized definition (string coercion, null-param guard, URI expansion).
   */
  fetchNodeDefinition(path?: string, ids?: IVisualizationNodeIds): Promise<unknown>;

  /** Return fields that should be omitted when configuring this entity */
  getOmitFormFields: () => string[];

  /** Given a path, update the model */
  updateModel(path: string | undefined, value: unknown): void;

  /** Add a step to the underlying Camel entity and return its path when supported. */
  addStep: (options: {
    definedComponent: DefinedComponent;
    mode: AddStepMode;
    data: IVisualizationNodeData;
    targetProperty?: string;
    /** When true and target is an array-clause (e.g. when), insert at start instead of end */
    insertAtStart?: boolean;
  }) => string | void;

  /** Given a path, get the content to be copied */
  getCopiedContent: (path?: string, ids?: IVisualizationNodeIds) => IClipboardContent | undefined;

  pasteStep: (options: {
    clipboardContent: IClipboardContent;
    mode: AddStepMode;
    data: IVisualizationNodeData;
    /** When true and target is an array-clause (e.g. when), insert at start instead of end */
    insertAtStart?: boolean;
  }) => void;

  /** Check if the node is draggable */
  canDragNode: (path?: string) => boolean;

  /** Check if the node is droppable */
  canDropOnNode: (path?: string) => boolean;

  /** Remove the step at a given path from the underlying Camel entity */
  removeStep: (path?: string) => void;

  /** Returns the NodeInteraction information so the UI can show whether this node can have children and/or siblings */
  getNodeInteraction(data: IVisualizationNodeData): NodeInteraction;

  /** Given a path, retrieve the Node validation status */
  getNodeValidationText(
    path?: string,
    schema?: KaotoSchemaDefinition['schema'],
    ids?: IVisualizationNodeIds,
  ): Promise<string | undefined>;

  /** Generates a IVisualizationNode from the underlying Camel entity */
  toVizNode: () => Promise<IVisualizationNode>;
}

export interface BaseEntityConstructor {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  new (...args: any[]): BaseEntity;
  isApplicable: (entity: unknown) => boolean;
}

/**
 * IVisualizationNode
 *
 * This interface is used to represent a node in the visualization tree.
 * Usually, each IVisualizationNode represent a component in a BaseVisualCamelEntity.
 *
 * This interface shouldn't know anything about the specific BaseVisualCamelEntity.
 */
export interface IVisualizationNode<T extends IVisualizationNodeData = IVisualizationNodeData> {
  id: string;
  data: T;
  lastUpdate: number;

  getId(): string | undefined;

  /** This method returns the label to be used by the canvas nodes */
  getNodeLabel(labelType?: NodeLabelType): string;

  /** This method returns the title used by the CanvasForm component */
  getNodeTitle(): string;

  addBaseEntityStep(
    definedComponent: DefinedComponent,
    mode: AddStepMode,
    targetProperty?: string,
    insertAtStart?: boolean,
  ): string | void;

  /** This method return the content to be copied for the node */
  getCopiedContent(): IClipboardContent | undefined;

  pasteBaseEntityStep(definedComponent: IClipboardContent, mode: AddStepMode, insertAtStart?: boolean): void;

  canDragNode(): boolean;

  canDropOnNode(): boolean;

  getNodeInteraction(): NodeInteraction;

  /** This method returns the node's associated schema asynchronously from the catalog */
  fetchSchema(): Promise<KaotoSchemaDefinition['schema'] | undefined>;

  /** Returns the node's normalized definition asynchronously */
  fetchNodeDefinition(): Promise<unknown>;

  /** Return fields that should be omitted when configuring this entity */
  getOmitFormFields(): string[];

  updateModel(value: unknown): void;
  switchComponentMode(processorName: string): boolean;

  getParentNode(): IVisualizationNode | undefined;

  setParentNode(parentNode?: IVisualizationNode): void;

  getPreviousNode(): IVisualizationNode | undefined;

  setPreviousNode(previousNode?: IVisualizationNode): void;

  getNextNode(): IVisualizationNode | undefined;

  setNextNode(node?: IVisualizationNode): void;

  getChildren(): IVisualizationNode[] | undefined;

  addChild(child: IVisualizationNode): void;

  removeChild(): void;

  /** Retrieve the node's validation status, relying into the underlying entity */
  getNodeValidationText(): Promise<string | undefined>;
}

/**
 * The three typed node identity fields shared between `IVisualizationNodeData`
 * and the `fetchNodeSchema` method signature.
 *
 * **Additive and hierarchical** — the identifiers form an ordered chain where
 * each level is only meaningful when the preceding level is present:
 *
 * ```
 * primaryNodeId                           ← always the anchor (if any id exists)
 *   └─ secondaryNodeId (requires primary) ← refines the primary
 *        └─ tertiaryNodeId (requires secondary, therefore also primary)
 * ```
 *
 * In other words:
 * - `secondaryNodeId` MUST NOT be set unless `primaryNodeId` is also set.
 * - `tertiaryNodeId` MUST NOT be set unless `secondaryNodeId` (and therefore
 *   `primaryNodeId`) is also set.
 *
 * All three fields are optional to accommodate nodes that carry no catalog
 * identity at all (e.g. placeholder nodes).
 *
 * See docs/GLOSSARY.md — "Primary / Secondary / Tertiary Node ID".
 */
export interface IVisualizationNodeIds {
  /**
   * The primary (first-level) identity of this node.
   *
   * This is the anchor: `secondaryNodeId` and `tertiaryNodeId` must not be
   * present unless this field is set.
   *
   * - In Camel Routes: the Processor name (e.g., 'to', 'choice', 'route').
   * - In Pipe: the Kamelet name directly.
   * - In Kamelet: the Processor name, same as Camel Routes.
   * - For Citrus: the test action name.
   * - For root group nodes: the entity type (e.g., 'route', 'routeConfiguration').
   */
  primaryNodeId?: NodeIdentity;

  /**
   * The secondary (second-level) identity of this node.
   *
   * Requires `primaryNodeId` to be present — must not be set in isolation.
   * `tertiaryNodeId` must not be present unless this field is also set.
   *
   * - In Camel Routes: the Camel Component (e.g., 'timer', 'amqp').
   * - In Pipe: not used.
   */
  secondaryNodeId?: NodeIdentity;

  /**
   * The tertiary (third-level) identity of this node.
   *
   * Requires both `secondaryNodeId` and `primaryNodeId` to be present —
   * must not be set unless the full preceding chain is in place.
   *
   * - In Camel Routes using a Kamelet URI: the Kamelet name (e.g., 'weather-source').
   * - In Pipe: not used (the Kamelet is already the primaryNodeId).
   */
  tertiaryNodeId?: NodeIdentity;
}

export interface IVisualizationNodeData extends IVisualizationNodeIds {
  name: string;
  path?: string;
  entity?: BaseVisualEntity;
  isPlaceholder: boolean;
  isGroup: boolean;
  /** Icon URL resolved from the catalog at mapping time */
  iconUrl: string;
  /** Display title resolved from the catalog at mapping time */
  title: string;
  /** Description/tooltip resolved from the catalog at mapping time */
  description: string;
  /** Tooltip for processor icon overlay (from/to/toD/poll) resolved from the catalog at mapping time */
  processorIconTooltip?: string;
  /** Schema resolved from the catalog at mapping time */
  schema?: KaotoSchemaDefinition['schema'];

  /** Normalized definition resolved at enrichment time */
  definition?: unknown;

  /** Alt text for the icon */
  iconAlt?: string;

  [key: string]: unknown;
}

export const enum AddStepMode {
  /** Used to append a new step before an existing step */
  PrependStep = 'prepend-step',
  /** Used to append a new step after an existing step */
  AppendStep = 'append-step',
  /** Used to replace an existing step */
  ReplaceStep = 'replace-step',
  /**
   * Used to insert a new step in the step property.
   * for instance, for adding steps to the `from` or `doTry` processor
   */
  InsertChildStep = 'insert-step',
  /**
   * Used to insert a new special step in a special step property.
   * for instance, for adding `when` clauses to the `choice.when` property,
   * or to add `doCatch` clauses to the `doTry.doCath` property
   */
  InsertSpecialChildStep = 'insert-special-step',
}

export interface NodeInteraction {
  canHavePreviousStep: boolean;
  canHaveNextStep: boolean;
  canHaveChildren: boolean;
  canHaveSpecialChildren: boolean;
  canReplaceStep: boolean;
  canRemoveStep: boolean;
  canRemoveFlow: boolean;
  canBeDisabled: boolean;
}

export const DISABLED_NODE_INTERACTION: NodeInteraction = {
  canHavePreviousStep: false,
  canHaveNextStep: false,
  canHaveChildren: false,
  canHaveSpecialChildren: false,
  canReplaceStep: false,
  canRemoveStep: false,
  canRemoveFlow: false,
  canBeDisabled: false,
};
